"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../../firebase"
import "./courses.css"
import { useNavigate } from "react-router-dom"
import { Star, Clock, Users, BookOpen } from "lucide-react"

const CoursesCard = ({ selectedCategory }) => {
  const [courses, setCourses] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true)
        const snap = await getDocs(collection(db, "courses"))
        const coursesData = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        setCourses(coursesData)
      } catch (error) {
        console.error("Error fetching courses:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchCourses()
  }, [])

  const navigate = useNavigate()

  useEffect(() => {
    if (selectedCategory === "all") {
      setFiltered(courses)
      return
    }

    const result = courses.filter((c) => {
      const type = (c.Type || "").toLowerCase().trim()
      const name = (c.ProgramName || "").toLowerCase()
      const desc = (c.ProgramDescription || "").toLowerCase()
      const category = (c.Category || "").toLowerCase().trim()
      const target = selectedCategory.toLowerCase()

      if (target === "all") return true

      // If the course has a valid category type, match it exactly
      const validTypes = ["ai", "animation", "marketing", "data", "vr", "robotics"]
      if (validTypes.includes(type)) {
        return type === target
      }

      // Direct matches
      if (type.includes(target) || category.includes(target)) return true

      // Slug mappings/keyword matches
      const keywordMap = {
        ai: ["ai", "artificial intelligence", "machine learning", "generative"],
        animation: ["animation", "vfx", "gaming", "graphics"],
        marketing: ["marketing", "social media", "content"],
        data: ["data science", "analytics", "python", "business"],
        vr: ["vr", "ar", "metaverse"],
        robotics: ["robotics", "automation", "iot"],
        bca: ["bca", "computer"],
        bba: ["bba", "business administration"],
        bsc: ["bsc", "science"]
      }

      if (keywordMap[target]) {
        return keywordMap[target].some(k => {
          if (k === "ai") {
            const regex = /\bai\b/i
            return regex.test(name) || regex.test(desc) || regex.test(type)
          }
          return name.includes(k) || desc.includes(k) || type.includes(k)
        })
      }

      return name.includes(target) || desc.includes(target)
    })
    setFiltered(result)
  }, [courses, selectedCategory])

  const titleMap = {
    ai: "AI & Machine Learning",
    animation: "Animation & VFX",
    marketing: "Digital Marketing",
    data: "Data Science & Analytics",
    vr: "AR / VR Technology",
    robotics: "Robotics & Automation",
    university: "University Courses",
    skp: "SKP Programs",
    languages: "Language Courses",
    ielts: "IELTS Preparation",
    bca: "BCA Programs",
    bba: "BBA Programs",
    bsc: "BSc Programs",
    all: "Professional Programs",
  }

  const getCategoryTheme = (category) => {
    const themes = {
      ai: "theme-purple",
      animation: "theme-cyan",
      marketing: "theme-orange",
      data: "theme-red",
      vr: "theme-blue",
      robotics: "theme-green",
      university: "theme-gold",
      skp: "theme-indigo",
    }
    return themes[category] || "theme-default"
  }

  if (loading) {
    return (
      <section className="coursesCard">
        <div className="courses-container">
          <div className="loading-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="loading-card" />
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="coursesCard">
      <div className="courses-container">
        <div className="courses-header">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="category-pill"
          >
            Programs
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="courses-title"
          >
            {titleMap[selectedCategory] || titleMap.all}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="courses-subtitle"
          >
            Explore our {filtered.length} specialized career-ready programs
          </motion.p>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state-centered">
            <div className="empty-state">
              <BookOpen size={48} className="empty-state-icon" />
              <h3 className="empty-state-title">Opening Soon</h3>
              <p className="empty-state-description">We are currently updating our {selectedCategory} programs. Stay tuned!</p>
            </div>
          </div>
        ) : (
          <div className="courses-grid">
            {filtered.map((course, idx) => {
              const theme = getCategoryTheme(selectedCategory)
              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -10 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`course-card-premium ${theme}`}
                  onClick={() => navigate(`/course/${course.id}`)}
                >
                  <div className="card-image-section">
                    <img
                      src={course.ImageURL || "https://picsum.photos/seed/course/800/600"}
                      alt={course.ProgramName}
                    />
                    <div className="card-overlay" />
                    <div className="card-badge-top">Course</div>
                    <div className="card-arrow">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  <div className="card-info-section">
                    <div className="card-rating">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={12} fill="#fbbf24" color="#fbbf24" />
                      ))}
                      <span>5.0</span>
                    </div>
                    <h3 className="card-title">{course.ProgramName}</h3>
                    <p className="card-desc">{course.ProgramDescription}</p>

                    <div className="card-footer">
                      <div className="meta">
                        <Clock size={14} />
                        <span>12 Weeks</span>
                      </div>
                      <div className="meta">
                        <Users size={14} />
                        <span>Enrolled</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

export default CoursesCard
